import React, { useEffect, useRef, useState } from "react";
import { graphql, useStaticQuery } from "gatsby";
import { CSSTransition } from "react-transition-group";
import styled from "styled-components";
import { KEY_CODES } from "@utils";
import { srConfig } from "@config";
import sr from "@utils/sr";
import { usePrefersReducedMotion } from "@hooks";

const StyledExperiencePage = styled.main`
  width: 100%;
`;

const HeroSection = styled.section`
  max-width: 1260px;
  margin: 0 auto;
  padding: 170px clamp(28px, 6vw, 80px) 90px;

  .eyebrow {
    margin: 0 0 18px;
    color: var(--text-muted);
    font-family: var(--font-mono);
    font-size: 13px;
    letter-spacing: 0.16em;
    text-transform: uppercase;
  }

  h1 {
    margin: 0 0 22px;
    color: var(--text-primary);
    font-size: clamp(42px, 6vw, 82px);
    line-height: 0.98;
    letter-spacing: -0.05em;
    max-width: 860px;
  }

  .lead {
    margin: 0;
    max-width: 780px;
    color: var(--text-secondary);
    font-size: clamp(18px, 2vw, 23px);
    line-height: 1.8;
  }

  .lead strong {
    color: var(--text-primary);
    font-weight: 600;
  }
`;

const SummaryStrip = styled.section`
  max-width: 1260px;
  margin: 0 auto;
  padding: 0 clamp(28px, 6vw, 80px) 95px;

  .wrap {
    display: grid;
    grid-template-columns: repeat(4, minmax(0, 1fr));
    border-top: 1px solid var(--border-light);
    border-bottom: 1px solid var(--border-light);

    @media (max-width: 920px) {
      grid-template-columns: repeat(2, minmax(0, 1fr));
    }

    @media (max-width: 560px) {
      grid-template-columns: 1fr;
    }
  }

  .item {
    padding: 24px 18px 26px;
    position: relative;
  }

  .item:not(:last-child):after {
    content: "";
    position: absolute;
    top: 18px;
    right: 0;
    width: 1px;
    height: calc(100% - 36px);
    background: var(--border-light);
  }

  .value {
    margin-bottom: 8px;
    color: var(--text-primary);
    font-size: clamp(28px, 4vw, 38px);
    font-weight: 600;
    letter-spacing: -0.04em;
  }

  .label {
    color: var(--text-secondary);
    font-size: 14px;
    line-height: 1.7;
    max-width: 220px;
  }

  @media (max-width: 920px) {
    .item:nth-child(2):after {
      display: none;
    }
  }

  @media (max-width: 560px) {
    .item:after {
      display: none !important;
    }
  }
`;

const SectionIntro = styled.div`
  max-width: 1260px;
  margin: 0 auto;
  padding: 0 clamp(28px, 6vw, 80px) 28px;

  .eyebrow {
    margin: 0 0 12px;
    color: var(--text-muted);
    font-family: var(--font-mono);
    font-size: 13px;
    letter-spacing: 0.16em;
    text-transform: uppercase;
  }

  h2 {
    margin: 0;
    color: var(--text-primary);
    font-size: clamp(34px, 4.6vw, 58px);
    line-height: 1.06;
    letter-spacing: -0.045em;
    max-width: 820px;
  }

  p {
    margin: 16px 0 0;
    max-width: 780px;
    color: var(--text-secondary);
    font-size: 17px;
    line-height: 1.9;
  }
`;

const ExperienceShell = styled.section`
  max-width: 1260px;
  margin: 0 auto;
  padding: 0 clamp(28px, 6vw, 80px) 150px;

  .inner {
    display: grid;
    grid-template-columns: 280px 1fr;
    gap: 22px;

    @media (max-width: 980px) {
      grid-template-columns: 1fr;
      gap: 18px;
    }
  }

  .fade-enter {
    opacity: 0;
    transform: translateY(6px);
  }

  .fade-enter-active {
    opacity: 1;
    transform: translateY(0);
    transition: opacity 220ms ease, transform 220ms ease;
  }

  .fade-exit {
    opacity: 1;
  }

  .fade-exit-active {
    opacity: 0;
    transition: opacity 160ms ease;
  }
`;

const StyledTabList = styled.div`
  position: sticky;
  top: calc(var(--nav-height) + 18px);
  align-self: start;

  border-radius: 24px;
  background: rgba(255, 255, 255, 0.42);
  border: 1px solid rgba(255, 255, 255, 0.55);
  backdrop-filter: blur(18px);
  -webkit-backdrop-filter: blur(18px);
  box-shadow:
    0 10px 30px rgba(0, 0, 0, 0.04),
    inset 0 1px 0 rgba(255, 255, 255, 0.35);
  overflow: hidden;

  @media (max-width: 980px) {
    position: relative;
    top: auto;
    display: flex;
    gap: 10px;
    padding: 12px;
    overflow-x: auto;
    border-radius: 20px;
    scroll-snap-type: x mandatory;
    -webkit-overflow-scrolling: touch;

    &::-webkit-scrollbar {
      height: 0;
    }
  }
`;

const StyledTabButton = styled.button`
  width: 100%;
  padding: 14px 16px;
  border: 0;
  background: ${({ isActive }) => (isActive ? "rgba(255,255,255,0.55)" : "transparent")};
  text-align: left;
  cursor: pointer;
  transition: var(--transition);

  display: flex;
  flex-direction: column;
  gap: 6px;

  @media (max-width: 980px) {
    min-width: 190px;
    border-radius: 16px;
    scroll-snap-align: start;
  }

  &:hover {
    background: rgba(255, 255, 255, 0.52);
  }

  &:focus-visible {
    outline: 2px solid rgba(17, 17, 17, 0.18);
    outline-offset: -2px;
    background: rgba(255, 255, 255, 0.58);
  }

  .company {
    font-family: var(--font-mono);
    font-size: 12px;
    letter-spacing: 0.08em;
    text-transform: uppercase;
    color: ${({ isActive }) => (isActive ? "var(--text-primary)" : "var(--text-secondary)")};
  }

  .meta {
    font-size: 12px;
    line-height: 1.35;
    color: var(--text-muted);
  }

  .type {
    display: inline-flex;
    align-items: center;
    width: fit-content;
    padding: 5px 9px;
    border-radius: 999px;
    font-family: var(--font-mono);
    font-size: 10px;
    line-height: 1;
    color: var(--text-secondary);
    border: 1px solid var(--border-light);
    background: rgba(255, 255, 255, 0.6);
  }
`;

const StyledHighlight = styled.div`
  position: absolute;
  left: 0;
  top: 0;
  width: 3px;
  height: 62px;
  border-radius: 999px;
  background: var(--sydneyblue);
  transform: translateY(calc(${({ activeTabId }) => activeTabId} * 62px));
  transition: transform 250ms cubic-bezier(0.2, 0.8, 0.2, 1);

  @media (max-width: 980px) {
    display: none;
  }
`;

const StyledTabPanels = styled.div`
  width: 100%;
`;

const StyledTabPanel = styled.div`
  padding: 28px;
  border-radius: 28px;
  background: rgba(255, 255, 255, 0.42);
  border: 1px solid rgba(255, 255, 255, 0.55);
  backdrop-filter: blur(20px);
  -webkit-backdrop-filter: blur(20px);
  box-shadow:
    0 12px 34px rgba(0, 0, 0, 0.045),
    inset 0 1px 0 rgba(255, 255, 255, 0.36);

  @media (max-width: 480px) {
    padding: 20px 18px;
    border-radius: 22px;
  }

  .header {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 18px;
    margin-bottom: 20px;
  }

  h3 {
    margin: 0;
    color: var(--text-primary);
    font-size: clamp(24px, 3vw, 34px);
    line-height: 1.08;
    letter-spacing: -0.03em;
    font-weight: 600;
  }

  .companyLink {
    color: var(--text-primary);
    font-weight: 600;
  }

  .sub {
    margin-top: 12px;
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
  }

  .pill {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 7px 10px;
    border-radius: 999px;
    font-family: var(--font-mono);
    font-size: 11px;
    color: var(--text-secondary);
    background: rgba(255, 255, 255, 0.6);
    border: 1px solid var(--border-light);
  }

  p,
  li {
    color: var(--text-secondary);
    line-height: 1.9;
    font-size: 15px;
  }

  p strong,
  li strong {
    color: var(--text-primary);
    font-weight: 600;
  }

  ul {
    margin: 18px 0 0;
    padding: 0;
    list-style: none;
  }

  li {
    position: relative;
    padding-left: 18px;
    margin-bottom: 12px;
  }

  li:last-child {
    margin-bottom: 0;
  }

  li:before {
    content: "";
    position: absolute;
    top: 11px;
    left: 0;
    width: 6px;
    height: 6px;
    border-radius: 999px;
    background: var(--sydneyblue);
  }

  a {
    color: var(--text-primary);
  }
`;

const formatRange = range => {
  if (!range) return "";

  const s = String(range).trim();

  if (s.includes("-") || /present/i.test(s)) return s;

  const now = new Date();
  const end = now.toLocaleString("en-AU", { month: "long", year: "numeric" });
  return `${s} - ${end}`;
};

const JobsExperience = () => {
  const data = useStaticQuery(graphql`
    query ExperiencePageJobsQuery {
      jobs: allMarkdownRemark(
        filter: { fileAbsolutePath: { regex: "/content/jobs/" } }
        sort: { fields: [frontmatter___date], order: DESC }
      ) {
        edges {
          node {
            frontmatter {
              title
              company
              location
              range
              url
              type
            }
            html
          }
        }
      }
    }
  `);

  const jobsData = data.jobs.edges || [];
  const [activeTabId, setActiveTabId] = useState(0);
  const [tabFocus, setTabFocus] = useState(null);
  const tabs = useRef([]);
  const revealContainer = useRef(null);
  const prefersReducedMotion = usePrefersReducedMotion();

  useEffect(() => {
    if (prefersReducedMotion || !revealContainer.current) return;
    sr.reveal(revealContainer.current, srConfig());
  }, [prefersReducedMotion]);

  useEffect(() => {
    if (tabFocus === null) return;

    if (tabs.current[tabFocus]) {
      tabs.current[tabFocus].focus();
      return;
    }
    if (tabFocus >= tabs.current.length) setTabFocus(0);
    if (tabFocus < 0) setTabFocus(tabs.current.length - 1);
  }, [tabFocus]);

  const onKeyDown = e => {
    switch (e.key) {
      case KEY_CODES.ARROW_UP: {
        e.preventDefault();
        setTabFocus((tabFocus ?? activeTabId) - 1);
        break;
      }
      case KEY_CODES.ARROW_DOWN: {
        e.preventDefault();
        setTabFocus((tabFocus ?? activeTabId) + 1);
        break;
      }
      default:
        break;
    }
  };

  const activeNode = jobsData?.[activeTabId]?.node;

  return (
    <ExperienceShell ref={revealContainer}>
      <div className="inner">
        <StyledTabList role="tablist" aria-label="Experience tabs" onKeyDown={onKeyDown}>
          {jobsData.map(({ node }, i) => {
            const { company, range, location, type } = node.frontmatter;

            return (
              <StyledTabButton
                key={i}
                type="button"
                isActive={activeTabId === i}
                onClick={() => {
                  setActiveTabId(i);
                  tabs.current[i]?.scrollIntoView?.({
                    behavior: "smooth",
                    inline: "start",
                    block: "nearest",
                  });
                }}
                ref={el => (tabs.current[i] = el)}
                id={`tab-${i}`}
                role="tab"
                tabIndex={activeTabId === i ? "0" : "-1"}
                aria-selected={activeTabId === i}
                aria-controls={`panel-${i}`}
              >
                <span className="company">{company}</span>
                {range ? <span className="meta">{formatRange(range)}</span> : null}
                {location ? <span className="meta">{location}</span> : null}
                {type ? <span className="type">{type}</span> : null}
              </StyledTabButton>
            );
          })}

          <StyledHighlight activeTabId={activeTabId} />
        </StyledTabList>

        <StyledTabPanels>
          {activeNode ? (
            <CSSTransition key={activeTabId} in timeout={250} classNames="fade" appear>
              <StyledTabPanel
                id={`panel-${activeTabId}`}
                role="tabpanel"
                tabIndex="0"
                aria-labelledby={`tab-${activeTabId}`}
              >
                {(() => {
                  const { frontmatter, html } = activeNode;
                  const { title, url, company, range, location, type } = frontmatter;

                  return (
                    <>
                      <div className="header">
                        <div>
                          <h3>
                            <span>{title}</span>{" "}
                            <span className="companyLink">
                              @{" "}
                              {url ? (
                                <a href={url} target="_blank" rel="noreferrer">
                                  {company}
                                </a>
                              ) : (
                                company
                              )}
                            </span>
                          </h3>

                          <div className="sub">
                            {range ? <span className="pill">{formatRange(range)}</span> : null}
                            {location ? <span className="pill">{location}</span> : null}
                            {type ? <span className="pill">{type}</span> : null}
                            {url ? (
                              <a className="pill" href={url} target="_blank" rel="noreferrer">
                                Visit
                              </a>
                            ) : null}
                          </div>
                        </div>
                      </div>

                      <div dangerouslySetInnerHTML={{ __html: html }} />
                    </>
                  );
                })()}
              </StyledTabPanel>
            </CSSTransition>
          ) : null}
        </StyledTabPanels>
      </div>
    </ExperienceShell>
  );
};

const ExperiencePageContent = () => {
  return (
    <StyledExperiencePage>
      <HeroSection>
        <p className="eyebrow">Experience</p>
        <h1>Building systems that hold up in real environments.</h1>
        <p className="lead">
          My experience spans <strong>analytics, software, data workflows, forecasting, reporting, and machine learning</strong>
          across roles where the work needed to be practical, reliable, and tied to real outcomes.
        </p>
      </HeroSection>

      <SummaryStrip>
        <div className="wrap">
          <div className="item">
            <div className="value">3M+</div>
            <div className="label">Payroll records processed across large-scale analytical systems.</div>
          </div>
          <div className="item">
            <div className="value">250k+</div>
            <div className="label">Work shifts analysed across compliance, roster, and payroll workflows.</div>
          </div>
          <div className="item">
            <div className="value">99.8%</div>
            <div className="label">Workflow acceleration delivered by reducing runtime from days to minutes.</div>
          </div>
          <div className="item">
            <div className="value">Multi-domain</div>
            <div className="label">Experience across legal, property, operations, analytics, and technical systems.</div>
          </div>
        </div>
      </SummaryStrip>

      <SectionIntro>
        <p className="eyebrow">Career Timeline</p>
        <h2>Roles, responsibilities, and measurable contribution.</h2>
        <p>
          A closer view of the environments I have worked in, the kinds of systems I built,
          and how my role evolved across analytics, operations, reporting, and technical delivery.
        </p>
      </SectionIntro>

      <JobsExperience />
    </StyledExperiencePage>
  );
};

export default ExperiencePageContent;